<template>
  <vx-card title="Procesos por tipo" id="card">
    <!-- LOADING -->
    <div
      v-if="uiState === 'loading'"
      class="gw-empty-state"
      style="height:350px;"
    >
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-empty-title">Cargando información…</div>
      <div class="gw-empty-subtitle">Un momento por favor</div>
    </div>

    <!-- READY (CHART) -->
    <VueApexCharts
      v-else-if="uiState === 'ready' && renderGraphic"
      type="donut"
      height="350"
      :options="chartOptions"
      :series="series"
    />

    <!-- EMPTY / ERROR -->
    <div
      v-else
      class="gw-empty-state"
      style="height:350px;"
    >
      <img
        class="gw-empty-img"
        :src="emptyStateImage"
        :alt="emptyStateAlt"
      />
      <div class="gw-empty-title">{{ emptyStateTitle }}</div>
      <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
    </div>
  </vx-card>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,

      requestKey: null,
      consultHandler: null,

      // Empty/Error illustrations (S3)
      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [],

      chartOptions: {
        labels: [],
        colors: [
          "#ffc300",
          "#5F0091",
          "#c88170",
          "#c5c870",
          "#5eab2c",
          "#ff3200",
          "#0d2cc8",
          "#c20dc8",
          "#bf0093",
          "#616a6b",
          "#0e6251",
          "#0e6251",
          "#d35400",
          "#17202a",
          "#f5cba7",
        ],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 200 },
              legend: { position: "bottom" },
            },
          },
        ],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(2) + "%";
          },
        },
      },
    };
  },

  components: {
    VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },

    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },

    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },

    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/projects-by-type?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        var params =
          "&start_date=" + this.startDate + "&end_date=" + this.endDate;
        url = this.url() + path + params;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) url += "&city_id=" + this.cityId;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [];
      this.chartOptions.labels = [];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    async setReady(labels, series) {
      // Remount para evitar glitches en ApexCharts
      this.renderGraphic = false;
      await this.$nextTick();

      this.chartOptions.labels = labels;
      this.series = series;

      // Si la suma es 0, se considera vacío
      var total = 0;
      for (var i = 0; i < this.series.length; i++) total += Number(this.series[i]) || 0;
      if (!total || total <= 0) {
        this.setEmpty();
        return;
      }

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[projects-by-type] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // Ignorar respuestas viejas
        if (this.requestKey !== key) {
          console.log("[projects-by-type] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[projects-by-type] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        var data = request.data;

        if (!Array.isArray(data) || data.length === 0) {
          console.warn("[projects-by-type] Empty data");
          this.setEmpty();
          return;
        }

        // Build labels + series (solo conteos > 0)
        var labels = [];
        var series = [];

        for (var i = 0; i < data.length; i++) {
          var row = data[i] || {};
          var count = row.count;

          if (typeof count === "string") count = parseFloat(count);
          count = typeof count === "number" && !isNaN(count) ? count : 0;

          if (count > 0) {
            labels.push(this.$t(row.name));
            series.push(count);
          }
        }

        if (!series.length) {
          console.warn("[projects-by-type] All counts are 0");
          this.setEmpty();
          return;
        }

        console.log("[projects-by-type] labels:", labels);
        console.log("[projects-by-type] series:", series);

        await this.setReady(labels, series);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[projects-by-type] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () =>
      this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
